import { useMemo, type JSX } from 'react'
import { useOrganisers, useQuery, useRegistry, useSections } from '@/hooks'
import type { EventFields } from '@/shared/schemas/event'
import { type Event, fields } from '@/shared/schemas/event'
import { constructQuery } from '@/hooks/index/useDocuments/queries/views/events'
import { eventTableColumns } from '@/views/EventsOverview/EventsListColumns'

import { Table } from '@/components/Table'
import { useDocuments } from '@/hooks/index/useDocuments'
import { SortingV1 } from '@ttab/elephant-api/index'
import { toast } from 'sonner'
import { getUTCDateRange } from '@/shared/datetime'
import { useTranslation } from 'react-i18next'

export const EventsList = (): JSX.Element => {
  const sections = useSections()
  const organisers = useOrganisers()
  const { locale, timeZone } = useRegistry()
  const { t } = useTranslation()
  const [query] = useQuery()
  const { from, to } = useMemo(() =>
    getUTCDateRange(query?.from ? new Date(query?.from as string) : new Date(), timeZone), [query, timeZone])

  const { error } = useDocuments<Event, EventFields>({
    documentType: 'core/event',
    query: constructQuery({ from, to }),
    fields,
    sort: [
      SortingV1.create({ field: 'document.meta.core_newsvalue.value', desc: true }),
      SortingV1.create({ field: 'document.meta.core_event.data.start', desc: false })
    ],
    options: {
      aggregatePages: true,
      setTableData: true,
      withStatus: true,
      withPlannings: true,
      subscribe: true
    }

  })

  const columns = useMemo(() => eventTableColumns({ sections, organisers, locale }, t), [sections, organisers, locale, t])

  if (error) {
    console.error('Error fetching event items:', error)
    toast.error(t('errors:toasts.getEventsFailed'))
  }

  return (
    <Table
      type='Event'
      columns={columns}
    />
  )
}
